import { findDataForNode, getReportAggregator } from 'components/reports/SchoolStatDetails/helpers';
import {
  ColumnsConfig,
  DefaultColumnConfig,
  GroupingColumnConfig,
} from 'components/shared/data/DataGrid/DataGrid.types';
import { Translator } from 'context/locale/LocaleContext/LocaleContext.types';
import {
  AggregationMethod,
  AggregationValue,
  isAggregationValue,
  StatDetailsColumn,
  StatDetailsRow,
} from 'store/reports/reports.types';
import { isString, isNumber } from 'lodash';

interface Params {
  columns: StatDetailsColumn[];
  trans: Translator;
  statName?: string;
  /**
   * When rendering group levels as multiple grid columns, group columns must stay visible.
   * In single-column grouping mode the group columns are hidden in favor of the auto group column.
   */
  hideGroupColumns?: boolean;
}

interface GroupingColumnParams {
  name?: string;
  statName?: string;
}

const normalizeValue = (value: any) => {
  if (value === undefined || value === null) return '';
  // AggregationValue objects implement `toString()` with formatted output (e.g. "0", "1 234").
  if (typeof value === 'object' && typeof value.toString === 'function') {
    return value.toString();
  }
  return value;
};

const extractNumeric = (raw: any): number | null => {
  const effective = isAggregationValue(raw) ? raw.value : raw;
  if (effective === undefined || effective === null || effective === '') return null;
  const parsed = Number(effective);
  return Number.isFinite(parsed) ? parsed : null;
};

const STAT_METHOD_OVERRIDES: Record<string, AggregationMethod> = {
  'Liczba uczniów': AggregationMethod.SUM,
  'Średnia ocen cząstkowych': AggregationMethod.AVG,
  'Liczba klas': AggregationMethod.SUM,
  'Liczba kont pracowników': AggregationMethod.SUM,
  'Liczba ocen': AggregationMethod.SUM,
  'Frekwencja uczniów': AggregationMethod.AVG,
  'Frekwencja według typów': AggregationMethod.SUM,
  'Średnia ocen semestralnych': AggregationMethod.AVG,
  'Liczba kont rodziców i uczniów': AggregationMethod.SUM,
  'Liczba realizacji': AggregationMethod.SUM,
  'Liczba zajęć pozaszkolnych': AggregationMethod.SUM,
};

const resolveFrontendMethod = (statName?: string, backendMethod?: AggregationMethod): AggregationMethod => {
  if (statName && STAT_METHOD_OVERRIDES[statName]) return STAT_METHOD_OVERRIDES[statName];
  if (backendMethod) return backendMethod;
  return AggregationMethod.SUM;
};

const aggregateFromLeafNodes = (node: any, field: string, method?: AggregationMethod): number | '' | null => {
  const leafNodes = Array.isArray(node?.allLeafChildren) ? node.allLeafChildren : [];
  if (!leafNodes.length) return null;

  const values = leafNodes
    .map((leaf) => extractNumeric(leaf?.data?.[field]))
    .filter((v): v is number => v !== null);

  switch (method) {
    case AggregationMethod.AVG:
      return values.length ? values.reduce((sum, v) => sum + v, 0) / values.length : 0;
    case AggregationMethod.COUNT:
      return values.length;
    case AggregationMethod.NONE:
      return '';
    case AggregationMethod.FIXED:
    case AggregationMethod.SUM:
    default:
      return values.reduce((sum, v) => sum + v, 0);
  }
};

export const statisticDetailsColumns: ColumnsConfig<StatDetailsRow, Params> = (params) => {
  const methodForStat = resolveFrontendMethod(params.statName);
  const isYearBreakdownView = params.statName === 'Podział wg roczników';
  const valueColumns = params.columns.filter((column) => !column.group);
  const lastValueColumnField = valueColumns[valueColumns.length - 1]?.field;

  return params.columns.reduce((result, column) => {
    if (column.group) {
      return [
        ...result,
        {
          id: column.field,
          field: column.field,
          name: () => column.name,
          columnParams: {
            field: column.field,
            rowGroup: true,
            hide: !column.name || (params.hideGroupColumns ?? true),
          },
        },
      ];
    } else {
      return [
        ...result,
        {
          id: column.field,
          name: () => column.name || undefined,
          cellClass: (params) => {
            return params.data?._isFake ? 'dim-cell' : '';
          },
          cellTooltip: (data, trans, formatter, { node, value }) => {
            if (node.aggData?.[column.field] && isAggregationValue(node.aggData?.[column.field])) {
              const method = node.aggData[column.field].aggregation.method as AggregationMethod;

              if (method === AggregationMethod.NONE) {
                return null;
              } else {
                return trans(`REPORTS.AGGREGATION_METHOD_TOOLTIP.${method.toUpperCase()}`);
              }
            }
            return value;
          },
          columnParams: {
            ...(isYearBreakdownView
              ? column.field === lastValueColumnField
                ? { minWidth: 90, flex: 1 }
                : { minWidth: 90, suppressSizeToFit: true }
              : {}),
            valueGetter: ({ data, context, node }) => {
              const isAggregatedColumn = column.aggregationField !== null;
              const directValue = data ? data[column.field] : undefined;

              if (isAggregatedColumn) {
                if (node?.group) {
                  const groupedValue = aggregateFromLeafNodes(node, column.field, methodForStat);
                  return groupedValue !== null ? groupedValue : 0;
                }
                if (directValue !== undefined && directValue !== null && directValue !== '') {
                  return directValue;
                }
                return 0;
              }

              // Non-aggregated columns: try backend/provided value first, then fallback to row-group lookup.
              if (directValue !== undefined && directValue !== null && directValue !== '') {
                return directValue;
              }

              const value = findDataForNode(node, column.field, context.rowGroupValues);
              return value !== undefined && value !== null ? value : '';
            },
            filterValueGetter: (params) => {
              const directValue = params.data ? params.data[column.field] : undefined;

              const isAggregatedColumn = column.aggregationField !== null;

              if (isAggregatedColumn) {
                if (params.node?.group) {
                  const groupedValue = aggregateFromLeafNodes(
                    params.node,
                    column.field,
                    methodForStat,
                  );
                  return groupedValue !== null ? normalizeValue(groupedValue) : '0';
                }
                if (directValue !== undefined && directValue !== null && directValue !== '') return normalizeValue(directValue);
                return '0';
              }

              if (directValue !== undefined && directValue !== null && directValue !== '') {
                return normalizeValue(directValue);
              }

              const groupData = params.context.rowGroupValues;

              let rowIdPointer = params.data?._parentId;
              let row: any = null;

              // Walk up the processed row group chain until we find a value.
              // Guard against missing `_parentId` to avoid infinite loops.
              while (rowIdPointer) {
                row = groupData.find((row) => row._id === rowIdPointer);
                if (row?.data?.[column.field] !== undefined) {
                  break;
                }
                rowIdPointer = row?._parentId;
              }

              return row?.data?.[column.field] !== undefined ? normalizeValue(row?.data?.[column.field]) : '';
            },
            valueFormatter: (params) => {
              const { value } = params;
              // Ensure group rows always display something for aggregated numeric columns.
              if (column.aggregationField !== null) {
                if (value === undefined || value === null || value === '') return '0';
                return normalizeValue(value);
              }

              // Our leaf values for aggregated columns are AggregationValue objects with custom toString().
              if (typeof value === 'object' && value !== null && typeof (value as any).toString === 'function') {
                return (value as any).toString();
              }

              return value;
            },
            aggFunc: column.aggregationField !== null ? getReportAggregator : null,
            comparator: (
              aggValueA: AggregationValue | string | number,
              aggValueB: AggregationValue | string | number,
            ) => {
              const valueA: string | number = isAggregationValue(aggValueA) ? aggValueA.value : aggValueA;
              const valueB: string | number = isAggregationValue(aggValueB) ? aggValueB.value : aggValueB;

              if (isString(valueA) && isString(valueB)) {
                return valueA.localeCompare(valueB);
              } else if (isNumber(valueA) && isNumber(valueB)) {
                return valueA - valueB;
              } else {
                return (valueA || '').toString().localeCompare((valueB || '').toString());
              }
            },
          },
        },
      ];
    }
  }, []);
};

export const statisticDetailsGroupingColumn: GroupingColumnConfig<any, GroupingColumnParams> = (params) => {
  const isYearBreakdownView = params?.statName === 'Podział wg roczników';

  return {
    ...(params?.name ? { name: () => params.name } : {}),
    columnParams: isYearBreakdownView ? { minWidth: 260, suppressSizeToFit: true } : { flex: 1 },
    rendererParams: {
      suppressCount: true,
    },
  };
};

export const defaultColumn: DefaultColumnConfig<any> = () => ({
  sortable: true,
  filter: true,
  suppressMenu: false,
  menuTabs: ['filterMenuTab'],
});
